package org.zq.astrock_ai.network

import org.zq.astrock_ai.util.formatDecimal

import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.statement.bodyAsText
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.zq.astrock_ai.data.StockProfile

/** 全市场股票名称、代码及拼音搜索，并补充实时行情。 */
class EastMoneyStockSearchClient(
    private val client: HttpClient = createMarketHttpClient(),
) {
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    suspend fun search(query: String, limit: Int = 15): List<StockProfile> {
        val keyword = query.trim()
        if (keyword.isBlank()) return emptyList()
        val text = client.get(SUGGEST_URL) {
            parameter("input", keyword)
            parameter("type", 14)
            parameter("count", limit.coerceIn(1, 30))
            parameter("token", SEARCH_TOKEN)
            header("User-Agent", UA)
            header("Referer", "https://quote.eastmoney.com/")
        }.bodyAsText()
        val rows = json.parseToJsonElement(text).jsonObject["QuotationCodeTable"]
            ?.jsonObject
            ?.get("Data")
            ?.jsonArray
            .orEmpty()
            .mapNotNull { element ->
                val item = element.jsonObject
                val code = item["Code"]?.jsonPrimitive?.contentOrNull.orEmpty()
                val name = item["Name"]?.jsonPrimitive?.contentOrNull.orEmpty()
                val quoteId = item["QuoteID"]?.jsonPrimitive?.contentOrNull.orEmpty()
                val classify = item["Classify"]?.jsonPrimitive?.contentOrNull.orEmpty()
                if (code.length != 6 || name.isBlank() || quoteId.isBlank() || classify != "AStock") {
                    null
                } else {
                    SearchHit(code, name, quoteId)
                }
            }
            .distinctBy { it.code }

        return coroutineScope {
            rows.map { hit ->
                async {
                    runCatching { fetchProfile(hit) }
                        .getOrElse { hit.toFallbackProfile() }
                }
            }.awaitAll()
        }
    }

    private suspend fun fetchProfile(hit: SearchHit): StockProfile {
        val text = client.get(STOCK_URL) {
            parameter("secid", hit.quoteId)
            parameter("fltt", 2)
            parameter("fields", "f43,f44,f45,f46,f47,f48,f57,f58,f116,f127,f169,f170")
            parameter("ut", MARKET_TOKEN)
            header("User-Agent", UA)
        }.bodyAsText()
        val data = json.parseToJsonElement(text).jsonObject["data"]?.jsonObject
            ?: return hit.toFallbackProfile()
        val price = data.number("f43")
        val changePercent = data.number("f170")
        val changeAmount = data.number("f169")
        val code = data.text("f57").ifBlank { hit.code }
        val name = data.text("f58").ifBlank { hit.name }
        return StockProfile(
            name = name,
            code = code,
            fullCode = fullCode(code),
            initial = name.take(1),
            avatarColor = avatarColor(code),
            price = price?.let { formatDecimal(it) } ?: "--",
            changePercent = changePercent?.let { "${if (it > 0) "+" else ""}${formatDecimal(it)}%" } ?: "--",
            changeAmount = changeAmount?.let { "${if (it > 0) "+" else ""}${formatDecimal(it)}" } ?: "--",
            isUp = (changePercent ?: 0.0) >= 0.0,
            industry = data.text("f127").ifBlank { "—" },
            marketCap = data.number("f116")?.let(::formatMoney) ?: "—",
            openPrice = data.number("f46")?.let { formatDecimal(it) } ?: "--",
            metrics = emptyList(),
            askLevels = emptyList(),
            bidLevels = emptyList(),
        )
    }

    private fun SearchHit.toFallbackProfile() = StockProfile(
        name = name,
        code = code,
        fullCode = fullCode(code),
        initial = name.take(1),
        avatarColor = avatarColor(code),
        price = "--",
        changePercent = "--",
        changeAmount = "--",
        isUp = true,
        industry = "—",
        marketCap = "—",
        openPrice = "--",
        metrics = emptyList(),
        askLevels = emptyList(),
        bidLevels = emptyList(),
    )

    private fun kotlinx.serialization.json.JsonObject.number(key: String): Double? =
        get(key)?.jsonPrimitive?.doubleOrNull

    private fun kotlinx.serialization.json.JsonObject.text(key: String): String =
        get(key)?.jsonPrimitive?.contentOrNull.orEmpty().takeUnless { it == "-" } ?: ""

    private fun fullCode(code: String): String = when {
        code.startsWith("6") -> "$code.SH"
        code.startsWith("8") || code.startsWith("4") -> "$code.BJ"
        else -> "$code.SZ"
    }

    private fun avatarColor(code: String): Long {
        val colors = longArrayOf(0xFFE53935, 0xFF2563EB, 0xFF7C3AED, 0xFF0891B2, 0xFFEA580C)
        return colors[(code.hashCode() and Int.MAX_VALUE) % colors.size]
    }

    private fun formatMoney(value: Double): String = when {
        kotlin.math.abs(value) >= 100_000_000 -> "${formatDecimal(value / 100_000_000)}亿"
        kotlin.math.abs(value) >= 10_000 -> "${formatDecimal(value / 10_000)}万"
        else -> formatDecimal(value, 0)
    }

    private data class SearchHit(
        val code: String,
        val name: String,
        val quoteId: String,
    )

    companion object {
        private const val SUGGEST_URL = "https://searchapi.eastmoney.com/api/suggest/get"
        private const val STOCK_URL = "https://push2.eastmoney.com/api/qt/stock/get"
        private const val SEARCH_TOKEN = "D43BF722C8E33BDC906FB84D85E326E8"
        private const val MARKET_TOKEN = "fa5fd1943c7b386f172d6893dbfba10b"
        private const val UA =
            "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
    }
}
